const STORAGE_PREFIX = 'bridge_mock_'
const ENABLED_KEY = `${STORAGE_PREFIX}enabled`

/**
 * Get mock override for a specific bridge action from localStorage.
 * Returns null if no override exists.
 */
export function getMockOverride(group: string, action: string): any | null {
  try {
    const key = `${STORAGE_PREFIX}${group}_${action}`
    const data = localStorage.getItem(key)
    if (!data) return null

    return JSON.parse(data)
  } catch {
    return null
  }
}

/**
 * Set mock override for a specific bridge action.
 */
export function setMockOverride(group: string, action: string, data: any): void {
  try {
    const key = `${STORAGE_PREFIX}${group}_${action}`
    localStorage.setItem(key, JSON.stringify(data))
  } catch {
    // Silent fail for SSR or quota exceeded
  }
}

/**
 * Remove a specific mock override.
 */
export function removeMockOverride(group: string, action: string): void {
  try {
    const key = `${STORAGE_PREFIX}${group}_${action}`
    localStorage.removeItem(key)
  } catch {
    // Silent fail for SSR
  }
}

/**
 * Get all current mock overrides as a map.
 * Returns: { 'Device.getDeviceInfo': {...}, 'User.getGender': {...}, ... }
 */
export function getAllMockOverrides(): Record<string, any> {
  const overrides: Record<string, any> = {}

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(STORAGE_PREFIX) || key === ENABLED_KEY) continue

      const data = localStorage.getItem(key)
      if (!data) continue

      // Extract group and action from key: bridge_mock_Device_getDeviceInfo
      const parts = key.substring(STORAGE_PREFIX.length).split('_')
      if (parts.length < 2) continue

      const group = parts[0]
      const action = parts.slice(1).join('_')
      const identifier = `${group}.${action}`

      try {
        overrides[identifier] = JSON.parse(data)
      } catch {
        // Skip invalid JSON
      }
    }
  } catch {
    // Return empty object on error
  }

  return overrides
}

/**
 * Clear all mock overrides from localStorage.
 */
export function clearAllMockOverrides(): void {
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX) && key !== ENABLED_KEY) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  } catch {
    // Silent fail for SSR
  }
}

/**
 * Check if mock overrides feature is enabled.
 * Stored as: bridge_mock_enabled = 'true' | 'false'
 */
export function isMockOverrideEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Toggle mock override feature on/off.
 */
export function setMockOverrideEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, enabled.toString())
  } catch {
    // Silent fail for SSR
  }
}
